import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { emitVisitCompleteWhenPhotosSettle, emitVisitError } from '../lib/embedBridge';
import { useHandoffSeed, skipHandoffContact, setAdvancedPeptideSession } from '@/lib/handoff';
import { usePreview, PreviewInterstitial } from './preview/PreviewShell';
import QuestionStep from './intake/QuestionStep';
import MultiSelectStep from './intake/MultiSelectStep';
import TextareaStep from './intake/TextareaStep';
import InputStep from './intake/InputStep';
import HeightWeightBmiStep from './intake/HeightWeightBmiStep';
import BasicContactStep from './intake/BasicContactStep';
import ShippingAddressStep from './intake/ShippingAddressStep';
import ConsentDropdownStep from './intake/ConsentDropdownStep';
import DisqualifiedStep from './intake/DisqualifiedStep';
import FileUploadStep from './intake/FileUploadStep';
import EmergencyContactStep from './intake/EmergencyContactStep';
import WaitlistStep from './intake/WaitlistStep';
import Footer from './intake/Footer';
import SquarePaymentForm from './intake/SquarePaymentForm';
import AccountSetupCta from './intake/AccountSetupCta';
import SecondaryConsentPrompt from './intake/SecondaryConsentPrompt';
import { RecommendationGate, BenefitList, TrustBanner, ProviderReviewNotice } from './intake/checkoutUi';
import BrandLogo from '@/components/BrandLogo';
import { buildTenantUrl, getTenantDashboardUrl } from '@/lib/tenant';
import { PRACTICE, PRACTITIONER_IMAGE, formatAnswersForSubmit, consentsForSubmit, withConsentHashes } from '../data/conciergeCommon';
import { apiService } from '../services/api';
import { describeSubmitFailure } from '../lib/utils';
import { MutatingDots } from 'react-loader-spinner';

// Dr. Mavroson's concierge programs (Advanced Peptide, Advanced Weight Loss
// Support, Advanced Longevity & Functional Medicine) — NOT Beluga visits. One
// form for all three, driven by a `program` from data/conciergePrograms.js:
// copy, billing, images, the question flow and its rules. Submits to the
// practice's own async-telehealth workflow (docs/BACKEND_CONCIERGE_PROGRAMS_PRD.md);
// membership charged via Square Subscriptions (card on file).

// The dashboard can't read this origin's sessionStorage, so the completion
// link carries the ids too (dashboard FE ask, 2026-09-28).
const dashboardLinkWithIds = (base, result) => {
  if (!base || !result || !result.intakeId) return base;
  try {
    const u = new URL(base, typeof window !== 'undefined' ? window.location.href : 'https://fitpal.com');
    u.searchParams.set('intakeId', String(result.intakeId));
    if (result.threadId) u.searchParams.set('threadId', String(result.threadId));
    return u.toString();
  } catch (e) {
    return base;
  }
};

// First step: what the membership is and includes (copy + photo from the
// practice's product page), then "Get started".
const ProgramOverview = ({ program, question, onNext }) => (
  <div data-testid="program-overview">
    <div className="rounded-3xl overflow-hidden border border-gray-200 mb-5 bg-white">
      <img src={program.image} alt={program.name} className="w-full h-auto block" />
    </div>
    <h2 className="text-2xl md:text-3xl font-semibold text-center text-gray-900 mb-1">{question.question}</h2>
    {question.subtitle && <p className="text-center text-gray-500 mb-4">{question.subtitle}</p>}
    <div className="flex items-baseline justify-center gap-2 mb-1">
      <span className="text-4xl font-bold text-[#2d8a5f]">${program.price}</span>
      <span className="text-gray-500">{program.billing.monthlyCents !== program.billing.todayCents ? 'first month · cancel any time' : '/month · cancel any time'}</span>
    </div>
    {program.billing.monthlyCents !== program.billing.todayCents && (
      <p className="text-center text-sm text-gray-500 mb-4">then ${program.billing.monthlyCents / 100}/month from month 2</p>
    )}
    <p className="text-gray-700 text-sm leading-relaxed mb-5 mt-3">{program.description}</p>
    <div className="rounded-2xl border border-gray-200 p-5 mb-3">
      <div className="flex items-center gap-3 mb-3">
        <img src={PRACTITIONER_IMAGE} alt={PRACTICE.practitioner} className="w-12 h-12 rounded-full object-cover object-top flex-shrink-0 bg-white border border-gray-200" />
        <div>
          <p className="font-semibold text-gray-900">{PRACTICE.practitioner}</p>
          <p className="text-xs text-gray-500">{PRACTICE.name}</p>
        </div>
      </div>
      <h3 className="font-semibold text-gray-900 mb-2">Membership includes</h3>
      <BenefitList items={program.includes} />
    </div>
    <p className="text-xs text-gray-400 mb-6">* {program.footnote}</p>
    <button type="button" onClick={onNext} className="btn-primary" data-testid="continue-button">Get started</button>
  </div>
);

const ConciergeProgramForm = ({ program }) => {
  const STORAGE_KEY = program.storageKey;
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved).answers || {} : {};
    } catch (e) { return {}; }
  });
  // §authenticated-patient handoff: prefill contact from the resolved ?pt= patient.
  useHandoffSeed(setAnswers);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [slideKey, setSlideKey] = useState(0);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [disqualifyReason, setDisqualifyReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const submittingRef = useRef(false);

  const preview = usePreview();
  const flow = useMemo(
    () => preview.wrapFlow(skipHandoffContact(program.buildFlow(answers))),
    [answers, preview, program],
  );
  const totalSteps = flow.length;
  const currentQuestion = flow[Math.min(currentStep, totalSteps - 1)];

  // Persist answers so a refresh doesn't lose a long intake; cleared on complete.
  useEffect(() => {
    if (isComplete || isDisqualified) return;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ answers, timestamp: Date.now() })); } catch (e) { /* no storage */ }
  }, [answers, isComplete, isDisqualified, STORAGE_KEY]);
  const clearSavedProgress = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* no storage */ }
  }, [STORAGE_KEY]);

  const stagingTest =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('belugaEnv') === 'staging';

  const handleDisqualify = useCallback((reason) => {
    setIsDisqualified(true);
    setDisqualifyReason(reason);
    clearSavedProgress();
    setSlideKey((p) => p + 1);
  }, [clearSavedProgress]);

  const handleNext = useCallback(() => {
    // Multi-selects only report on Continue — re-check them here.
    if (currentQuestion) {
      const dq = program.isDisqualifyingAnswer(currentQuestion.id, answers[currentQuestion.id]);
      if (dq.disqualified) { handleDisqualify(dq.reason); return; }
    }
    if (currentStep < totalSteps - 1) {
      setSlideKey((p) => p + 1);
      setCurrentStep((p) => p + 1);
    }
  }, [currentStep, totalSteps, currentQuestion, answers, handleDisqualify, program]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setSlideKey((p) => p + 1);
      setCurrentStep((p) => p - 1);
    }
  }, [currentStep]);

  const handleAnswer = useCallback(
    (questionId, answer) => {
      setAnswers((prev) => ({ ...prev, [questionId]: answer }));
      const dq = program.isDisqualifyingAnswer(questionId, answer);
      if (dq.disqualified) { handleDisqualify(dq.reason); return; }
      if (currentQuestion?.type === 'single-select') setTimeout(() => {
        setSlideKey((p) => p + 1);
        setCurrentStep((p) => p + 1);
      }, 200);
    },
    [currentQuestion, handleDisqualify, program],
  );

  const handleStartOver = useCallback(() => {
    clearSavedProgress();
    setCurrentStep(0);
    setAnswers({});
    setIsComplete(false);
    setIsDisqualified(false);
    setDisqualifyReason('');
    setSlideKey((p) => p + 1);
  }, [clearSavedProgress]);

  const submitOrder = useCallback(
    async (payment) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      try {
        setIsSubmitting(true);
        const contact = answers['basic-contact'] || {};
        const address = answers['shipping-address'] || {};
        const emergency = answers['emergency-contact'] || {};
        const paymentId =
          payment?.paymentId || payment?.payment_id || localStorage.getItem('fitpal_intake_payment_id') || '';

        const submitted = await apiService.submitAdvancedPeptideIntake({
          program: program.key,
          answers: formatAnswersForSubmit(answers, flow),
          contact: {
            first_name: contact.name?.split(' ')[0] || '',
            last_name: contact.name?.split(' ').slice(1).join(' ') || '',
            email: contact.email || '',
            phone: (contact.phone || '').replace(/\D/g, ''),
            dob: contact.dob || '',
            sex: answers['sex-at-birth'] || '',
          },
          emergencyContact: {
            first_name: emergency.firstName || '', last_name: emergency.lastName || '',
            phone: (emergency.phone || '').replace(/\D/g, ''), relationship: emergency.relationship || '',
          },
          address: { address: address.address1 || '', address2: address.address2 || '', city: address.city || '', state: address.state || '', zip: address.zipCode || '' },
          stateOfResidence: answers['state-of-residence'] || '',
          contactPreference: answers['contact-preference'] || '',
          consents: await withConsentHashes(consentsForSubmit(answers)),
          flags: program.flags(answers),
          paymentId,
          planId: program.planSlug,
        });

        // Government ID → the practice's own file store (no Beluga relay).
        const idPhoto = answers['identity-verification'];
        if (idPhoto && idPhoto.preview && submitted?.intakeId) {
          apiService
            .uploadAdvancedPeptideFile({ intakeId: submitted.intakeId, slideId: 'identity-verification', photo: idPhoto, paymentId })
            .catch((err) => console.warn(`[${program.key}] ID upload failed:`, err?.response?.data || err.message));
        }

        setResult(submitted);
        // Keep the ids for this tab (dashboard ask, 2026-09-28) and hand them to
        // an embedding dashboard via postMessage.
        setAdvancedPeptideSession({ intakeId: submitted?.intakeId, threadId: submitted?.threadId });
        emitVisitCompleteWhenPhotosSettle({
          masterId: submitted?.intakeId, visitId: submitted?.intakeId, visitType: program.primaryGoal,
          intakeId: submitted?.intakeId, threadId: submitted?.threadId,
        });
        clearSavedProgress();
        setIsComplete(true);
        setSlideKey((p) => p + 1);
        setCurrentStep((p) => Math.min(p + 1, totalSteps - 1));
      } catch (error) {
        console.error(`${program.name} submission error:`, error);
        emitVisitError(error, { visitType: program.primaryGoal });
        alert(describeSubmitFailure(error));
      } finally {
        submittingRef.current = false;
        setIsSubmitting(false);
      }
    },
    [answers, flow, totalSteps, clearSavedProgress, program],
  );

  const renderStep = () => {
    if (isDisqualified) {
      return (
        <div key={slideKey} className="slide-wrapper slide-forward">
          <DisqualifiedStep reason={disqualifyReason} onStartOver={handleStartOver} />
        </div>
      );
    }
    if (!currentQuestion) return null;

    const commonProps = {
      question: preview.withIcons(currentQuestion),
      value: answers[currentQuestion.id],
      onAnswer: (a) => handleAnswer(currentQuestion.id, a),
      onNext: handleNext,
      onBack: handleBack,
      canGoBack: currentStep > 0,
      isLastStep: currentStep === totalSteps - 1,
      setLoading,
    };
    const { billing } = program;
    const introThenMonthly = billing.monthlyCents !== billing.todayCents;

    const content = (() => {
      switch (currentQuestion.type) {
        case 'program-overview':
          return <ProgramOverview program={program} question={currentQuestion} onNext={handleNext} />;
        case 'single-select':
          return <QuestionStep {...commonProps} />;
        case 'multi-select':
          return <MultiSelectStep {...commonProps} />;
        case 'textarea':
          return <TextareaStep {...commonProps} />;
        case 'input':
        case 'number':
        case 'text':
          return <InputStep {...commonProps} />;
        case 'height-weight-bmi':
          return <HeightWeightBmiStep {...commonProps} onDisqualify={handleDisqualify} isOnTreatment={false} />;
        case 'basic-contact':
          return <BasicContactStep {...commonProps} minAge={18} />;
        case 'emergency-contact':
          return <EmergencyContactStep {...commonProps} />;
        case 'shipping-address':
          return <ShippingAddressStep {...commonProps} />;
        case 'file-upload':
          return <FileUploadStep {...commonProps} />;
        case 'consent-dropdown':
          return <ConsentDropdownStep {...commonProps} onDisqualify={handleDisqualify} />;
        case 'waitlist':
          return <WaitlistStep question={currentQuestion} state={answers['state-of-residence']} onJoined={clearSavedProgress} />;
        case 'preview-interstitial':
          return <PreviewInterstitial step={currentQuestion} onNext={handleNext} />;
        case 'checkout':
          return (
            <RecommendationGate
              product={{ name: program.name, sub: `Physician-led plan by ${PRACTICE.practitioner}`, price: billing.priceLabel }}
              headline={program.recommendHeadline}
              subhead={`Start your membership and ${PRACTICE.practitionerShort} will review your intake and message you within 24–48 hours.`}
              aboutItems={program.includes.slice(0, 4)}
              selectLabel="Start my membership"
            >
              <div data-testid="concierge-checkout" className="max-w-md mx-auto">
                <div className="mb-5">
                  <p className="text-[#2d8a5f] font-semibold text-sm">You're in!</p>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">Start your membership.</h2>
                </div>

                <div className="rounded-3xl border border-[#2d8a5f]/30 bg-[#f0faf4] p-5 mb-3">
                  <div className="flex items-center gap-4 pb-4 border-b border-[#2d8a5f]/15">
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-gray-900 leading-snug">{program.name}</p>
                      <p className="text-sm text-gray-500">Monthly membership · cancel any time</p>
                    </div>
                    <span className="text-2xl font-bold text-[#2d8a5f] flex-shrink-0">
                      ${billing.todayCents / 100}<span className="text-sm font-medium text-gray-500">{introThenMonthly ? ' today' : '/mo'}</span>
                    </span>
                  </div>
                  <BenefitList className="pt-4" items={program.includes} />
                  <p className="text-xs text-gray-400 mt-3">* {program.footnote}</p>
                </div>

                <ProviderReviewNotice className="mb-4" />
                <TrustBanner>
                  <span className="font-semibold">Cancel within 2 days for a full refund.</span> After that, your membership auto-renews monthly; cancel any time with 30 days' notice.
                </TrustBanner>

                <div className="rounded-2xl border border-gray-200 p-5 mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">{program.name}{introThenMonthly ? ' · first month' : ''}</span><span className="font-medium">{billing.todayLine}</span>
                  </div>
                  {introThenMonthly && (
                    <div className="flex justify-between text-sm mb-2" data-testid="then-monthly">
                      <span className="text-gray-600">From month 2</span><span className="font-medium">${billing.monthlyCents / 100}.00 / month</span>
                    </div>
                  )}
                  <p className="text-xs text-gray-400">{billing.summary} Medications, labs and other services are billed separately.</p>
                  <div className="border-t border-gray-200 mt-2 pt-3 flex justify-between items-center">
                    <span className="font-semibold text-lg">Total today</span>
                    <span className="text-2xl font-bold text-[#2d8a5f]" data-testid="cart-total">{billing.todayLine}</span>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">Payment details</h3>
                  {stagingTest ? (
                    <div className="space-y-3">
                      <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        ⚠️ Staging sign-off mode — Square is bypassed. Submits a test intake with no charge and no membership.
                      </p>
                      <button type="button" data-testid="staging-bypass-pay" disabled={isSubmitting} onClick={() => submitOrder({ paymentId: 'STAGING_TEST_BYPASS' })} className="btn-primary">
                        {isSubmitting ? 'Submitting…' : '[Staging test] Skip payment & submit'}
                      </button>
                    </div>
                  ) : (
                    <SquarePaymentForm
                      planId={program.planSlug}
                      amount={billing.todayCents}
                      cardOnFile
                      onComplete={submitOrder}
                      answers={answers}
                      setLoading={setLoading}
                      clearSavedProgress={clearSavedProgress}
                      primaryGoal={program.primaryGoal}
                      currentStep={currentStep}
                      timestamp={Date.now()}
                      email={answers['basic-contact']?.email}
                    />
                  )}
                </div>
              </div>
            </RecommendationGate>
          );
        case 'complete':
          return (
            <div className="max-w-xl mx-auto text-center py-10" data-testid="concierge-complete">
              <div className="w-16 h-16 bg-[#e8f5ee] rounded-full flex items-center justify-center mx-auto mb-5">
                <svg className="w-8 h-8 text-[#2d8a5f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                Thanks{answers['basic-contact']?.name ? `, ${answers['basic-contact'].name.split(' ')[0]}` : ''}!
              </h2>
              <p className="text-gray-500 mb-4">
                {PRACTICE.practitioner} will review your intake and message you in your patient dashboard within 24–48 hours.
              </p>
              {result?.intakeId && (
                <div className="inline-flex flex-col items-center bg-gray-50 rounded-xl px-6 py-3 mb-4">
                  <span className="text-xs uppercase tracking-wide text-gray-400">Reference</span>
                  <span className="text-lg font-semibold text-gray-900" data-testid="concierge-ref">{result.intakeId}</span>
                </div>
              )}
              <ol className="text-left max-w-md mx-auto space-y-3 text-sm text-gray-700 mb-6 list-decimal list-inside">
                <li><span className="font-semibold">Set your password</span> — your username is your email.</li>
                <li><span className="font-semibold">Upload your insurance card</span> in your dashboard and enter the name and member ID exactly as printed on it.</li>
                <li><span className="font-semibold">Watch for {PRACTICE.practitionerShort}'s message</span> with your personalized plan.</li>
              </ol>
              <SecondaryConsentPrompt />
              <AccountSetupCta email={answers['basic-contact']?.email} />
              <a href={dashboardLinkWithIds(getTenantDashboardUrl(), result)} className="inline-block mt-4 text-sm text-[#2d8a5f] underline underline-offset-2">Go to my dashboard</a>
            </div>
          );
        default:
          return <QuestionStep {...commonProps} />;
      }
    })();

    return (
      <div key={slideKey} className="slide-wrapper slide-forward">
        {preview.frame(currentQuestion, content, handleBack)}
      </div>
    );
  };

  return (
    <>
      <div className="intake-form-loader" style={{ display: loading ? '' : 'none' }}>
        <MutatingDots />
      </div>
      <div className={`min-h-screen flex flex-col ${preview.rootClass}`} data-testid={`${program.key}-form`} style={{ display: loading === false ? '' : 'none' }}>
        <header className="w-full py-4 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            {currentStep > 0 && !isComplete && !isDisqualified ? (
              <button onClick={handleBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0" data-testid="back-button" style={{ display: currentQuestion?.type === 'complete' ? 'none' : '' }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                <span className="text-sm font-medium">Back</span>
              </button>
            ) : (
              <a href={buildTenantUrl('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0" data-testid="home-link">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                <span className="text-sm font-medium">Home</span>
              </a>
            )}
            <div className="text-center flex-1">
              <BrandLogo alt="FitPal" className="mx-auto" style={{ width: '400px', height: 'auto', maxWidth: '100%' }} />
              <p className="text-xs text-gray-500 mt-1">{program.name} · {PRACTICE.practitioner}</p>
            </div>
            <div className="w-16 flex-shrink-0" />
          </div>
        </header>

        {!isComplete && !isDisqualified && (
          <div className="w-full px-4 py-2">
            <div className="max-w-2xl mx-auto">{preview.progress(currentQuestion, currentStep, totalSteps)}</div>
          </div>
        )}

        <main className="flex-1 flex flex-col items-center justify-start px-4 py-8">
          <div className="w-full max-w-2xl slide-container">{renderStep()}</div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ConciergeProgramForm;
